import { useAutomationRules } from 'client/store'
import {
  removeAutomationAction,
  saveAutomationRule,
} from 'client/store/automationRules/automationRulesActions'
import automationRulesSelectors from 'client/store/automationRules/automationRulesSelectors'
import { localeNames } from 'common/constants/localeTypes'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import {
  ActionIdType,
  ActionSaveType,
  AutomationRuleActionsEnum,
} from 'src/editor/shared/types/automation-rules/actions-types'
import { TriggerKeyType } from 'src/editor/shared/types/automation-rules/triggers-types'
import { CourseAccessTypeEnum } from 'src/editor/shared/types/course'
import { ResourceBaseNumberOptionInterface } from 'src/editor/shared/types/resources-interface'
import {
  PrimaryButton,
  SecondaryButton,
} from 'src/editor/shared/ui-lib/buttons/button'
import { SelectNew } from 'src/editor/shared/ui-lib/select-new/select-new'
import { getLocalesOptions } from 'src/editor/shared/utils/get-locales-options'
import {
  getResourceBaseNameOptions,
  getResourceBaseSubjectOptions,
} from 'src/editor/shared/utils/resources-utils'
import { EntityIdType } from 'src/shared/schemas/entity-id.schema'
import { AutomationRuleTriggersEnum } from '../../../types/automation-rules/triggers-types'
import { TextInputControl } from '../../controls/text-input-control'
import { useGetDefaultAction } from '../hooks/use-get-default-action'
import { validateAutomationAction } from '../utils/validate-automation-action'
import { CourseAccessSelect } from './course-access-type-select'
import { useResources } from './use-resources'
import { getResourceSelectedOption } from './utils/get-resource-selected-option'
import { updateAction } from './utils/update-action'

type AddOrEditActionProps = {
  onClose: () => void
  entityId: EntityIdType
  triggerType: AutomationRuleTriggersEnum
  triggerKey: TriggerKeyType
  actionId?: ActionIdType | undefined
  actionType?: AutomationRuleActionsEnum | undefined
}

export const AddOrEditAction = ({
  onClose,
  actionId,
  actionType,
  entityId,
  triggerType,
  triggerKey,
}: AddOrEditActionProps) => {
  const { t } = useTranslation()

  const [actionState, setActionState] = useState<ActionSaveType | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  )
  const dispatch = useDispatch()

  const getDefaultAction = useGetDefaultAction()
  const action = useAutomationRules(
    automationRulesSelectors.getAutomationRuleActionByEntityId(
      entityId,
      triggerKey,
      actionId,
    ),
  )

  const {
    tagsResourcesQueryResult,
    campaignsResourcesQueryResult,
    emailsResourcesQueryResult,
    specificEmailsResourcesQueryResult,
    coursesResourcesQueryResult,
    courseBundlesResourcesQueryResult,
    communitiesResourcesQueryResult,
  } = useResources({ actionType })

  useEffect(() => {
    if (action) {
      setActionState(action)
    } else {
      setActionState(getDefaultAction(actionType as AutomationRuleActionsEnum))
    }
  }, [action, actionType])

  const onDelete = () => {
    if (actionState) {
      dispatch(
        removeAutomationAction({
          entityId,
          triggerKey,
          automationTriggerType: triggerType,
          actionId: actionState.id,
        }),
      )
      onClose()
    }
  }

  const onSave = () => {
    if (actionState) {
      setErrorMessage(undefined)
      const errorMessage = validateAutomationAction(actionState, t)

      if (errorMessage) {
        setErrorMessage(errorMessage)
        return
      }

      dispatch(
        saveAutomationRule({
          entityId,
          triggerKey,
          automationTriggerType: triggerType,
          automationAction: actionState,
        }),
      )
      onClose()
    }
  }

  const commonProperties = {
    itemStyles: { padding: '0px' },
    menuPortalTarget: document.body,
    error: errorMessage,
  } as const

  let component: React.ReactNode | null = null

  switch (actionState?.type) {
    case AutomationRuleActionsEnum.add_tag:
    case AutomationRuleActionsEnum.remove_tag: {
      component = (
        <SelectNew
          {...commonProperties}
          onChange={option =>
            setActionState(
              updateAction({
                action: actionState,
                option: option as ResourceBaseNumberOptionInterface,
              }),
            )
          }
          options={getResourceBaseNameOptions(
            tagsResourcesQueryResult.data ?? [],
          )}
          labelText="entity_settings.tag.title"
          selectedOption={getResourceSelectedOption(
            actionState,
            getResourceBaseNameOptions(tagsResourcesQueryResult.data ?? []),
            actionState.tag.id,
          )}
          placeholder={t('entity_settings.automation_rule.choose_tag')}
          isLoading={tagsResourcesQueryResult.isLoading}
        />
      )
      break
    }
    case AutomationRuleActionsEnum.subscribe_to_campaign:
    case AutomationRuleActionsEnum.unsubscribe_from_campaign:
      component = (
        <SelectNew
          {...commonProperties}
          onChange={option =>
            setActionState(updateAction({ action: actionState, option }))
          }
          options={getResourceBaseNameOptions(
            campaignsResourcesQueryResult.data ?? [],
          )}
          placeholder={t('entity_settings.automation_rule.choose_campaign')}
          labelText="entity_settings.campaign.title"
          selectedOption={getResourceSelectedOption(
            actionState,
            getResourceBaseNameOptions(
              campaignsResourcesQueryResult.data ?? [],
            ),
            actionState.campaign.id,
          )}
          isLoading={campaignsResourcesQueryResult.isLoading}
        />
      )
      break
    case AutomationRuleActionsEnum.send_email:
      component = (
        <SelectNew
          {...commonProperties}
          onChange={option =>
            setActionState(updateAction({ action: actionState, option }))
          }
          options={getResourceBaseSubjectOptions(
            emailsResourcesQueryResult.data ?? [],
          )}
          labelText="entity_settings.email.title"
          selectedOption={getResourceSelectedOption(
            actionState,
            getResourceBaseSubjectOptions(
              emailsResourcesQueryResult.data ?? [],
            ),
            actionState.systemeEmail.id,
          )}
          placeholder={t('entity_settings.automation_rule.choose_email')}
          isLoading={emailsResourcesQueryResult.isLoading}
        />
      )
      break
    case AutomationRuleActionsEnum.send_email_to_specific_address:
      component = (
        <SelectNew
          {...commonProperties}
          onChange={option =>
            setActionState(updateAction({ action: actionState, option }))
          }
          options={getResourceBaseSubjectOptions(
            specificEmailsResourcesQueryResult.data ?? [],
          )}
          labelText="entity_settings.email.title"
          placeholder={t('entity_settings.automation_rule.choose_email')}
          selectedOption={getResourceSelectedOption(
            actionState,
            getResourceBaseSubjectOptions(
              specificEmailsResourcesQueryResult.data ?? [],
            ),
            actionState.emailMessageWithRecipient.id,
          )}
          isLoading={specificEmailsResourcesQueryResult.isLoading}
        />
      )
      break
    case AutomationRuleActionsEnum.enroll_in_course:
      component = (
        <>
          <SelectNew
            {...commonProperties}
            onChange={option =>
              setActionState(updateAction({ action: actionState, option }))
            }
            options={getResourceBaseNameOptions(
              coursesResourcesQueryResult.data ?? [],
            )}
            labelText="entity_settings.courses.title"
            selectedOption={getResourceSelectedOption(
              actionState,
              getResourceBaseNameOptions(
                coursesResourcesQueryResult.data ?? [],
              ),
              actionState.course.id,
            )}
            placeholder={t('entity_settings.automation_rule.choose_course')}
            isLoading={coursesResourcesQueryResult.isLoading}
          />
          <CourseAccessSelect
            onChangeAccessType={data =>
              setActionState(
                updateAction({ action: actionState, accessType: data }),
              )
            }
            type={actionState.type}
            value={
              actionState.courseAccessType || CourseAccessTypeEnum.fullAccess
            }
          />
        </>
      )
      break
    case AutomationRuleActionsEnum.revoke_access_to_course:
      component = (
        <SelectNew
          onChange={option =>
            setActionState(updateAction({ action: actionState, option }))
          }
          options={getResourceBaseNameOptions(
            coursesResourcesQueryResult.data ?? [],
          )}
          labelText="entity_settings.courses.title"
          selectedOption={getResourceSelectedOption(
            actionState,
            getResourceBaseNameOptions(coursesResourcesQueryResult.data ?? []),
            actionState.course.id,
          )}
          placeholder={t('entity_settings.automation_rule.choose_course')}
          isLoading={coursesResourcesQueryResult.isLoading}
        />
      )
      break
    case AutomationRuleActionsEnum.revoke_access_to_course_bundle:
      component = (
        <SelectNew
          {...commonProperties}
          onChange={option =>
            setActionState(updateAction({ action: actionState, option }))
          }
          options={getResourceBaseNameOptions(
            courseBundlesResourcesQueryResult.data ?? [],
          )}
          labelText="entity_settings.course_bundle.title_one"
          selectedOption={getResourceSelectedOption(
            actionState,
            getResourceBaseNameOptions(
              courseBundlesResourcesQueryResult.data ?? [],
            ),
            actionState.courseBundle.id,
          )}
          placeholder={t(
            'entity_settings.automation_rule.choose_course_bundle',
          )}
          isLoading={courseBundlesResourcesQueryResult.isLoading}
        />
      )
      break
    case AutomationRuleActionsEnum.enroll_in_course_bundle:
      component = (
        <>
          <SelectNew
            {...commonProperties}
            onChange={option =>
              setActionState(updateAction({ action: actionState, option }))
            }
            options={getResourceBaseNameOptions(
              courseBundlesResourcesQueryResult.data ?? [],
            )}
            labelText="entity_settings.course_bundle.title_one"
            selectedOption={getResourceSelectedOption(
              actionState,
              getResourceBaseNameOptions(
                courseBundlesResourcesQueryResult.data ?? [],
              ),
              actionState.courseBundle.id,
            )}
            placeholder={t(
              'entity_settings.automation_rule.choose_course_bundle',
            )}
            isLoading={courseBundlesResourcesQueryResult.isLoading}
          />
          <CourseAccessSelect
            onChangeAccessType={data =>
              setActionState(
                updateAction({ action: actionState, accessType: data }),
              )
            }
            type={actionState.type}
            value={
              actionState.enrollmentAccessType ||
              CourseAccessTypeEnum.fullAccess
            }
          />
        </>
      )
      break
    case AutomationRuleActionsEnum.grant_access_to_community:
      component = (
        <SelectNew
          {...commonProperties}
          onChange={option =>
            setActionState(updateAction({ action: actionState, option }))
          }
          options={getResourceBaseNameOptions(
            communitiesResourcesQueryResult.data ?? [],
          )}
          labelText="entity_settings.community.title"
          selectedOption={getResourceSelectedOption(
            actionState,
            getResourceBaseNameOptions(
              communitiesResourcesQueryResult.data ?? [],
            ),
            actionState.community.id,
          )}
          placeholder={t('entity_settings.automation_rule.choose_community')}
          isLoading={communitiesResourcesQueryResult.isLoading}
        />
      )
      break
    case AutomationRuleActionsEnum.revoke_access_to_community:
      component = (
        <SelectNew
          {...commonProperties}
          onChange={option =>
            setActionState(updateAction({ action: actionState, option }))
          }
          options={getResourceBaseNameOptions(
            communitiesResourcesQueryResult.data ?? [],
          )}
          labelText="entity_settings.community.title"
          selectedOption={getResourceSelectedOption(
            actionState,
            getResourceBaseNameOptions(
              communitiesResourcesQueryResult.data ?? [],
            ),
            actionState.community.id,
          )}
          placeholder={t('entity_settings.automation_rule.choose_community')}
          isLoading={communitiesResourcesQueryResult.isLoading}
        />
      )
      break
    case AutomationRuleActionsEnum.send_webhook:
      component = (
        <TextInputControl
          update={value =>
            setActionState(updateAction({ action: actionState, value }))
          }
          placeholder={t('entity_settings.automation_rule.type_webhook')}
          labelText="entity_settings.automation_rule.webhook_url"
          value={actionState.webhookUrl}
          error={errorMessage}
        />
      )
      break
    case AutomationRuleActionsEnum.create_user:
      component = (
        <SelectNew
          {...commonProperties}
          onChange={option =>
            setActionState(updateAction({ action: actionState, option }))
          }
          options={getLocalesOptions(t)}
          labelText="entity_settings.automation_rule.action.new_user_language"
          selectedOption={
            actionState.locale
              ? {
                  value: actionState.locale,
                  label: t(localeNames[actionState.locale]),
                }
              : null
          }
          placeholder={t(
            'entity_settings.automation_rule.choose_language_of_user',
          )}
        />
      )
      break
  }
  return (
    <div>
      {component}
      <div className="mt-6 flex gap-3">
        <SecondaryButton onClick={onDelete}>
          {t('entity_settings.automation_rule.delete_action')}
        </SecondaryButton>
        <PrimaryButton onClick={onSave}>
          {t('entity_settings.automation_rule.save_action')}
        </PrimaryButton>
      </div>
    </div>
  )
}
